export function formatCurrency(value) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value));
}

export function getUserTimezone() {
  return localStorage.getItem('centralmax_timezone') || 'America/Sao_Paulo';
}

export function setUserTimezone(tz) {
  localStorage.setItem('centralmax_timezone', tz);
}

export function formatDate(dateStr) {
  if (!dateStr) return '-';
  const str = String(dateStr).trim();
  const date = new Date(str.replace(' ', 'T') + '-03:00');
  if (isNaN(date.getTime())) return '-';
  const tz = getUserTimezone();
  return date.toLocaleString('pt-BR', {
    timeZone: tz,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * O texto do backend virado `Date`, ou `null`.
 *
 * Existe pelo mesmo motivo do `formatDateOnly` logo abaixo: o backend entrega
 * `"2026-09-01 14:32:00"` SEM fuso, e `new Date()` sobre isso adota o fuso do
 * navegador. Quem precisa CALCULAR com as duas pontas (quanto tempo o prestador
 * levou entre sair e chegar) não pode formatar e subtrair texto; precisa da data
 * de verdade, lida com a mesma regra que o resto da tela usa para exibir.
 */
export function paraData(dateStr) {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return isNaN(dateStr.getTime()) ? null : dateStr;
  const data = new Date(String(dateStr).trim().replace(' ', 'T') + '-03:00');
  return isNaN(data.getTime()) ? null : data;
}

/**
 * Só a hora, "14:32".
 *
 * Para lista de eventos do MESMO chamado, a data completa em cada linha é ruído:
 * o operador está olhando um atendimento que começou há vinte minutos e o que
 * ele quer comparar é hora com hora.
 */
export function formatHora(dateStr) {
  const data = paraData(dateStr);
  if (!data) return '-';
  return data.toLocaleTimeString('pt-BR', {
    timeZone: getUserTimezone(), hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Só a data, sem hora, com o mesmo tratamento de fuso do `formatDate`.
 *
 * Existe porque metade das telas queria só a data e, na falta desta função,
 * cada uma escreveu a sua com `new Date(valor).toLocaleDateString('pt-BR')`.
 * Isso parece inofensivo e não é: o backend entrega `"2026-08-16 10:00:00"`
 * SEM fuso (ver `api/src/db/conversores.ts`), e `new Date()` sobre um texto
 * assim usa o fuso do navegador de quem está olhando. Num operador fora de
 * Brasília, o chamado das 23h aparece no dia seguinte, e ninguém desconfia de
 * uma data que parece uma data.
 */
export function formatDateOnly(dateStr) {
  if (!dateStr) return '-';
  const str = String(dateStr).trim();
  // Data pura ("2026-08-16") não tem hora para converter, e passar por fuso
  // aqui é justamente o que empurraria o dia para trás.
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [a, m, d] = str.split('-');
    return `${d}/${m}/${a}`;
  }
  const date = new Date(str.replace(' ', 'T') + '-03:00');
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR', {
    timeZone: getUserTimezone(),
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatCpfCnpj(value) {
  if (!value) return '-';
  const clean = value.replace(/\D/g, '');
  if (clean.length === 11) {
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (clean.length === 14) {
    return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
}

export function formatTelefone(value) {
  if (!value) return '-';
  const clean = value.replace(/\D/g, '');
  if (clean.length === 11) {
    return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (clean.length === 10) {
    return clean.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return value;
}

export function formatPlaca(value) {
  if (!value) return '-';
  const upper = value.toUpperCase();
  if (upper.length === 7 && !upper.includes('-')) {
    return upper.slice(0, 3) + '-' + upper.slice(3);
  }
  return upper;
}

export function formatMinutes(value) {
  if (value === null || value === undefined || value === '') return '-';
  const min = Number(value);
  if (isNaN(min)) return '-';
  if (min < 60) return `${Math.round(min)}min`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m > 0 ? `${h}h${m}min` : `${h}h`;
}

/**
 * Escapa o que vai para dentro do HTML do PDF.
 *
 * Sem isto, um dado com `<` ou `&` corrompe a tabela em silêncio: "veículo <em>
 * parado" perde o "<em>" porque o navegador o lê como tag, e "Silva & Cia" gera
 * HTML inválido. Nome de empresa com "&" é comum, e o relatório sai com um
 * pedaço faltando sem ninguém entender por quê.
 */
function paraHtml(valor) {
  // Vazio vira traco, igual ao Excel. Sem isto, a mesma celula sai "-" na
  // planilha e em BRANCO no PDF, e duas saidas do mesmo clique discordam sobre
  // se o dado falta ou nao.
  if (valor === null || valor === undefined || valor === '') return '-';
  return String(valor)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Marca uma celula de planilha como NUMERO, para quem monta a exportacao.
 *
 * O driver do PostgreSQL entrega NUMERIC como texto de proposito, para nao
 * perder centavo convertendo para ponto flutuante. Isso esta certo no
 * transporte, e estava errado na planilha: a celula ia como texto, e coluna de
 * texto no Excel nao entra em SOMA, nao alinha à direita e ordena "9,00" depois
 * de "10,00". A exportacao financeira falhava no primeiro uso que alguem faz
 * dela, que é somar a coluna de valor.
 *
 * A conversao é DECLARADA pela coluna, e nao adivinhada aqui dentro. Tentar
 * descobrir o tipo olhando o conteudo transformaria um CPF sem mascara em
 * numero e comeria o zero à esquerda; documento, placa e protocolo sao cadeias
 * de digitos que NAO sao numeros, e so quem escreveu a coluna sabe disso.
 *
 * Vazio vira `-`, e nao 0: célula em branco e valor zero sao coisas diferentes,
 * e somar zeros inventados num relatorio financeiro é pior que a lacuna.
 */
export function numeroDePlanilha(valor) {
  if (valor === null || valor === undefined || valor === '') return '-';
  const n = Number(valor);
  return Number.isFinite(n) ? n : '-';
}

export function exportExcel(headers, rows, filename, titulo) {
  return import('xlsx').then((XLSX) => {
    const wb = XLSX.utils.book_new();

    // Build data array: titulo + blank row + headers + rows
    const sheetData = [];
    if (titulo) {
      sheetData.push([titulo]);
      sheetData.push([]);
    }
    sheetData.push(headers);
    rows.forEach(row => sheetData.push(row.map(cell => (cell === null || cell === undefined || cell === '') ? '-' : cell)));

    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Column widths based on content
    const colWidths = headers.map((h, ci) => {
      let max = h.length;
      rows.forEach(row => {
        const len = String(row[ci] ?? '').length;
        if (len > max) max = len;
      });
      return { wch: Math.min(Math.max(max + 2, 8), 40) };
    });
    ws['!cols'] = colWidths;

    // Merge titulo across all columns
    if (titulo) {
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Dados');
    XLSX.writeFile(wb, (filename || 'export') + '.xlsx');
    return true;
  });
}

/**
 * Gera PDF via janela de impressão com tabela formatada.
 * @param {string[]} headers - Nomes das colunas
 * @param {any[][]} rows - Linhas de dados
 * @param {string} titulo - Título do relatório
 */
export function exportPdf(headers, rows, titulo) {
  const style = `<style>
    @page { size: landscape; margin: 12mm; }
    body { font-family: 'Inter', -apple-system, sans-serif; font-size: 11px; color: #1d1d1f; }
    h1 { font-size: 16px; margin: 0 0 4px; }
    .sub { font-size: 11px; color: #8e8e93; margin: 0 0 12px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f5f5f7; font-weight: 700; text-align: left; padding: 6px 8px; border-bottom: 2px solid #ddd; font-size: 10px; text-transform: uppercase; letter-spacing: 0.03em; }
    td { padding: 5px 8px; border-bottom: 1px solid #eee; }
    td.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
    tr:nth-child(even) td { background: #fafafa; }
    .footer { margin-top: 16px; font-size: 9px; color: #8e8e93; text-align: center; }
  </style>`;

  /*
   * Numero alinha à direita e sai escrito em português; o resto alinha à
   * esquerda. Coluna de valor com casas decimais desencontradas não se lê: o
   * olho compara a posição da vírgula, e não o texto.
   *
   * Quem decide o que é número é a própria coluna, que já declarou isso com
   * `numeroDePlanilha` para o Excel funcionar. Aqui só se aproveita a mesma
   * declaração, em vez de adivinhar de novo olhando o conteúdo: placa, CNPJ e
   * protocolo são cadeias de dígitos que não são números.
   *
   * `toLocaleString` sem casas fixas é de propósito: contagem continua "12", e
   * dinheiro vira "1.234,56". Forçar duas casas transformaria "12 atendimentos"
   * em "12,00".
   */
  const celula = (valor) => (typeof valor === 'number'
    ? `<td class="num">${paraHtml(valor.toLocaleString('pt-BR'))}</td>`
    : `<td>${paraHtml(valor)}</td>`);

  const tableHtml = `<table><thead><tr>${headers.map(h => `<th>${paraHtml(h)}</th>`).join('')}</tr></thead><tbody>${rows.map(row => `<tr>${row.map(celula).join('')}</tr>`).join('')}</tbody></table>`;

  const now = new Date().toLocaleString('pt-BR');
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${paraHtml(titulo || 'Relatório')}</title>${style}</head><body>
    <h1>${paraHtml(titulo || 'Relatório')}</h1>
    <p class="sub">Gerado em ${now} · ${rows.length} registro(s) · CentralMax</p>
    ${tableHtml}
    <div class="footer">CentralMax — Sistema de Gestão 24 Horas</div>
  </body></html>`;

  const win = window.open('', '_blank');

  /*
   * Bloqueador de pop-up devolve `null`, e o código antigo simplesmente não
   * fazia nada. Do lado do operador isso é pior que um erro: ele clica em
   * "PDF", nada acontece, clica de novo, e conclui que o sistema está quebrado.
   *
   * Devolver `false` deixa quem chamou avisar — e o aviso precisa dizer o que
   * fazer, porque "permitir pop-up" não é óbvio para quem só quer um relatório.
   */
  if (!win) return false;

  win.document.write(html);
  win.document.close();
  setTimeout(() => { win.print(); }, 400);
  return true;
}
